import {
  Box,
  Flex,
  Link,
  ListItem,
  UnorderedList,
  useColorModeValue,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { useFormikContext } from "formik";
import React from "react";
import useScrollSpy from "../../hooks/useScrollSpy";
import useSectionValidation from "../../hooks/useSectionValidation";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { FormSectionStatus } from "../../lib/models/form-section-status.model";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { CheckCircle } from "../icons/CheckCircle";
import { TimesCircle } from "../icons/TimesCircle";

export type FormNavigationProps = {
  sections: FormSectionStatus[];
  actionHandler?: (action: QuoteFormActions) => void;
};

function FormNavigation({ sections }: FormNavigationProps) {
  const { touched, errors, dirty } = useFormikContext();
  const textColor = useColorModeValue("gray.600", "white");
  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);

  const [validatedSections] = useSectionValidation(
    sections,
    touched,
    errors,
    dirty
  );

  const [activeId] = useScrollSpy(
    sections.map(({ id }) => `[id="${id}"]`),
    350,
    "main-container"
  );

  const getStatusColor = (status: "success" | "error" | "info") => {
    if (status === "success") {
      return "green.500";
    } else if (status === "error") {
      return "red.500";
    }

    return "blue";
  };

  const getStatusIcon = (status: "success" | "error" | "info") => {
    if (status === "success") {
      return <CheckCircle boxSize={4} fill={getStatusColor(status)} />;
    } else if (status === "error") {
      return <TimesCircle boxSize={4} fill={getStatusColor(status)} />;
    } else {
      return (
        <Flex
          backgroundColor="gray.300"
          height={4}
          width={4}
          borderRadius="50%"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            borderRadius="50%"
            height="8px"
            width="8px"
            backgroundColor="brand.500"
          ></Box>
        </Flex>
      );
    }
  };

  return (
    <>
      <Box
        display={{ base: "none", "2xl": "block" }}
        as="aside"
        position="sticky"
        top="175px"
        left={0}
        flex="none"
        height="full"
        borderRadius="md"
        marginRight={4}
        background={bgColor}
        minWidth="200px"
      >
        <Flex justifyContent="space-between">
          <nav>
            <UnorderedList listStyleType="none" marginLeft={0}>
              {validatedSections.map((section, index) => (
                <ListItem
                  flex="1"
                  whiteSpace="nowrap"
                  transition="padding 200ms ease-out"
                  padding={1}
                  key={section.value}
                  color={activeId === section.id ? "brand.300" : textColor}
                  fontWeight={activeId === section.id ? "500" : "400"}
                >
                  <Flex alignItems="center" padding={2} borderRadius="lg">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height={5}
                      width={5}
                      marginRight={4}
                      borderRadius="50%"
                    >
                      {section.icon ? (
                        <>{section.icon}</>
                      ) : (
                        <>{getStatusIcon(section.status)}</>
                      )}
                    </Box>
                    <Link fontSize="sm" href={`#${section.id}`}>
                      {section.label}
                    </Link>
                  </Flex>
                </ListItem>
              ))}
            </UnorderedList>
          </nav>
        </Flex>
      </Box>
    </>
  );
}

export default FormNavigation;

export const SmallBlockHeading = styled.p`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.25px;
  text-transform: uppercase;
  margin-top: var(--chakra-space-2);
  margin-bottom: var(--chakra-space-2);
  padding-left: var(--chakra-space-2);
  padding-right: var(--chakra-space-2);
  color: var(--chakra-colors-gray-600);
`;
